import React, { useEffect, useState } from "react";
import { getBackendUrl } from "../utils/paths";
import "./List.module.css";

const statusColors = {
  Pending: "#f59e0b",
  Shortlisted: "#3b82f6",
  Interview: "#8b5cf6",
  Selected: "#10b981",
  Rejected: "#ef4444",
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("adminToken");
  if (!token) return {};

  return {
    Authorization: `Bearer ${token}`,
    "X-Auth-Token": token,
  };
};

export default function CareerApplicants() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    const fetchApplicants = async () => {
      try {
        const response = await fetch(
          getBackendUrl("/backend/api/admin/applications/index.php"),
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || "Failed to fetch applications");
        }

        const data = await response.json();
        setApplicants(data.items || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchApplicants();
  }, []);

  const filteredApplicants = applicants.filter((app) => {
    if (filterStatus === "all") return true;
    return app.application_status === filterStatus;
  });

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(
        getBackendUrl(`/backend/api/admin/applications/index.php?id=${id}`),
        {
          method: "PUT",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ application_status: newStatus }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to update status");
      }

      setApplicants((prev) =>
        prev.map((app) =>
          app.id === id ? { ...app, application_status: newStatus } : app
        )
      );
    } catch (err) {
      alert("Error updating status: " + err.message);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        <div style={{ textAlign: "center", color: "#64748b", fontSize: "1.1rem" }}>
          Loading applications...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px" }}>
        <div style={{ color: "#ef4444", fontSize: "1.1rem" }}>
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "1.875rem", fontWeight: "700", marginBottom: "16px", color: "#0f172a" }}>
          Career Applicants
        </h1>

        {/* Filter Section */}
        <div style={{ marginBottom: "16px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            onClick={() => setFilterStatus("all")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
              fontSize: "0.9rem",
              fontWeight: "500",
              backgroundColor: filterStatus === "all" ? "#10b981" : "#e2e8f0",
              color: filterStatus === "all" ? "#fff" : "#0f172a",
              transition: "all 0.2s",
            }}
          >
            All ({applicants.length})
          </button>
          {["Pending", "Shortlisted", "Interview", "Selected", "Rejected"].map((status) => {
            const count = applicants.filter((a) => a.application_status === status).length;
            return (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  backgroundColor: filterStatus === status ? statusColors[status] : "#e2e8f0",
                  color: filterStatus === status ? "#fff" : "#0f172a",
                  transition: "all 0.2s",
                }}
              >
                {status} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {filteredApplicants.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: "40px 20px",
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          color: "#64748b",
        }}>
          <p style={{ fontSize: "1.1rem" }}>No applications found</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "#fff",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Name</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Email</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Phone</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Job Title</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Experience</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Status</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Resume</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", color: "#0f172a" }}>Applied Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplicants.map((app) => (
                <tr key={app.id} style={{ borderBottom: "1px solid #e2e8f0", transition: "background-color 0.2s" }}>
                  <td style={{ padding: "12px", color: "#0f172a", fontWeight: "500" }}>{app.full_name}</td>
                  <td style={{ padding: "12px", color: "#64748b", fontSize: "0.9rem" }}>
                    <a href={`mailto:${app.email}`} style={{ color: "#10b981", textDecoration: "none" }}>
                      {app.email}
                    </a>
                  </td>
                  <td style={{ padding: "12px", color: "#64748b", fontSize: "0.9rem" }}>{app.phone_number}</td>
                  <td style={{ padding: "12px", color: "#0f172a", fontWeight: "500" }}>{app.job_id || "N/A"}</td>
                  <td style={{ padding: "12px", color: "#64748b", fontSize: "0.9rem" }}>
                    {app.total_experience} {app.total_experience === "0" || app.total_experience === 0 ? "Fresher" : "years"}
                  </td>
                  <td style={{ padding: "12px" }}>
                    <select
                      value={app.application_status || "Pending"}
                      onChange={(e) => updateStatus(app.id, e.target.value)}
                      style={{
                        padding: "6px 8px",
                        borderRadius: "4px",
                        border: `1px solid ${statusColors[app.application_status] || "#e2e8f0"}`,
                        backgroundColor: statusColors[app.application_status] || "#f8fafc",
                        color: "#fff",
                        fontWeight: "600",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                      }}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Shortlisted">Shortlisted</option>
                      <option value="Interview">Interview</option>
                      <option value="Selected">Selected</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </td>
                  <td style={{ padding: "12px" }}>
                    {app.resume ? (
                      <a
                        href={getBackendUrl(app.resume)}
                        download={`resume_${app.id}`}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#10b981",
                          color: "#fff",
                          borderRadius: "4px",
                          textDecoration: "none",
                          fontSize: "0.85rem",
                          fontWeight: "600",
                          display: "inline-block",
                        }}
                      >
                        Download
                      </a>
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No file</span>
                    )}
                  </td>
                  <td style={{ padding: "12px", color: "#64748b", fontSize: "0.9rem" }}>
                    {new Date(app.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
